import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../lib/supabase';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Activity, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Helper to safely extract error message
const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error?.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
    }
    return JSON.stringify(detail);
  }
  return error?.message || 'Error desconocido';
};

export default function NFCClaim() {
  const { token } = useParams();
  const { user, setReturnUrl } = useAuth();
  const navigate = useNavigate();
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    validateToken();
  }, [token]);

  useEffect(() => {
    if (validation && validation.valid && user && user.role === 'owner') {
      claimPet();
    }
  }, [user, validation]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/nfc/${token}/validate`);
      setValidation(response.data);
      
      if (!user && response.data.valid) {
        setReturnUrl(`/nfc/${token}`);
        toast.info(`Debes iniciar sesión para vincular a ${response.data.pet_name}`);
        setTimeout(() => {
          navigate('/owner/login');
        }, 2000);
      }
    } catch (err) {
      console.error('NFC validation error:', err);
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const claimPet = async () => {
    setClaiming(true);
    try {
      const authToken = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/api/nfc/${token}/claim`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      
      toast.success(`¡${response.data.pet_name} ha sido vinculado exitosamente!`);
      setTimeout(() => {
        navigate(`/owner/pets/${response.data.pet_id}`);
      }, 1500);
    } catch (err) {
      console.error('NFC claim error:', err);
      const errorMsg = getErrorMessage(err);
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center bg-white border border-slate-200">
          <Activity className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Validando código NFC...</h2>
          <p className="text-slate-600">Por favor espera un momento</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center bg-white border border-slate-200">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Código No Válido</h2>
          <div className="text-slate-600 mb-6">
            {typeof error === 'object' ? (
              <pre className="text-left bg-slate-100 p-3 rounded text-xs overflow-auto">
                {JSON.stringify(error, null, 2)}
              </pre>
            ) : (
              <p>{String(error)}</p>
            )}
          </div>
          <Button onClick={() => navigate('/')} variant="outline">
            Volver al Inicio
          </Button>
        </Card>
      </div>
    );
  }

  if (claiming) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center bg-white border border-slate-200">
          <Activity className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Vinculando mascota...</h2>
          <p className="text-slate-600">Estamos registrando a {validation?.pet_name} en tu cuenta</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center px-4">
      <Card className="max-w-md w-full p-8 text-center bg-white border border-slate-200">
        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">¡Vinculación Exitosa!</h2>
        <p className="text-slate-600 mb-6">
          {validation?.pet_name} ha sido vinculado a tu cuenta
        </p>
        <Button onClick={() => navigate('/owner')} className="w-full bg-blue-600 hover:bg-blue-700">
          Ver Mis Mascotas
        </Button>
      </Card>
    </div>
  );
}
